/**
 * Sign up — the invited account-claim page (`/signup`).
 *
 * Launch is invite-driven: parents, chefs, and school admins get an email
 * pointing here with `?email=` prefilled. Unlike `/get-started` (the
 * school-first organic funnel), this page asks nothing about schools — an
 * invited user's org/household relationships were provisioned when the
 * invite was created, and the server reconciles their pending invitations
 * at signup. Uninvited emails are rejected by the invited-only signup gate
 * (`not_invited`) and pointed at /get-started instead.
 *
 * Two-phase submit, both to `/api/auth/signup` (no school, no roles):
 * the first POST answers `{ otpRequired: true }` after emailing a
 * 6-character verification code (inbox proof — without it, knowing an
 * invited email would be enough to squat the account); the second POST
 * carries `otpCode` and completes the signup. On success we immediately
 * log in with the new credentials and drop into the app.
 */

import { Alert, AlertDescription } from '@ordo/ui/alert'
import { Card } from '@ordo/ui/card'
import { Field, FieldLabel } from '@ordo/ui/field'
import { Input } from '@ordo/ui/input'
import { LoadingButton } from '@ordo/ui/loading-button'
import { PasswordInput } from '@ordo/ui/password-input'
import { usePostHog } from '@posthog/react'
import { Info } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router'

import { ExistingAccountAlert } from '@/components/existing-account-alert'
import { PublicShell } from '@/components/public-shell'
import { formatPhone, isValidEmail, isValidPhone, signinHrefForEmail } from '@/lib/contact-fields'

type SignupErrorCode = 'exists' | 'not_invited' | 'otp_invalid' | 'unexpected'

export function SignupPage() {
  const posthog = usePostHog()
  const [params] = useSearchParams()

  const [email, setEmail] = useState(() => params.get('email')?.trim() ?? '')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorCode, setErrorCode] = useState<SignupErrorCode | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Verify step: set once the server has emailed the code.
  const [otpRequired, setOtpRequired] = useState(false)
  const [otpCode, setOtpCode] = useState('')

  const passwordsMatch = password.length > 0 && password === confirm
  const formValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    isValidEmail(email) &&
    isValidPhone(phone) &&
    password.length >= 8 &&
    passwordsMatch

  async function handleSubmit(includeCode: boolean) {
    setSubmitting(true)
    setErrorCode(null)
    setErrorMessage(null)
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim(),
          phone: phone.trim(),
          password,
          roles: [],
          schoolSlug: '',
          activationCode: '',
          ...(includeCode && otpCode.trim() ? { otpCode: otpCode.trim() } : {}),
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        otpRequired?: boolean
        code?: string
        error?: string
      }
      if (!res.ok || !data.ok) {
        setErrorCode(
          data.code === 'exists' || data.code === 'not_invited' || data.code === 'otp_invalid'
            ? data.code
            : 'unexpected',
        )
        setErrorMessage(data.error ?? null)
        return
      }
      if (data.otpRequired) {
        setOtpRequired(true)
        setOtpCode('')
        return
      }
      posthog.capture('signup_completed', { invited: true })

      // Self-signups are auto-verified — log in immediately and drop into
      // the app instead of showing a "check your email" screen.
      const loginRes = await fetch('/api/auth/credentials', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      })
      const loginData = (await loginRes.json().catch(() => ({}))) as { ok?: boolean; redirectTo?: string }
      window.location.href = loginRes.ok && loginData.ok ? (loginData.redirectTo ?? '/') : signinHrefForEmail(email)
    } catch {
      setErrorCode('unexpected')
    } finally {
      setSubmitting(false)
    }
  }

  if (otpRequired) {
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
            <p className="text-sm text-muted-foreground">
              We sent a 6-character code to <span className="font-medium text-foreground">{email.trim()}</span>. Enter
              it below to finish creating your account.
            </p>
          </header>

          {errorCode === 'otp_invalid' ? (
            <p className="text-sm text-destructive">
              {errorMessage ?? 'That code is incorrect or has expired. Check the email we sent, or resend the code.'}
            </p>
          ) : errorCode ? (
            <p className="text-sm text-destructive">{errorMessage ?? 'Unexpected error. Please try again.'}</p>
          ) : null}

          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSubmit(true)
            }}>
            <Field>
              <FieldLabel htmlFor="signup-otp">Verification code</FieldLabel>
              <Input
                id="signup-otp"
                autoComplete="one-time-code"
                autoFocus
                placeholder="K3JF7Q"
                className="uppercase tracking-widest"
                value={otpCode}
                onChange={(e) => {
                  setOtpCode(e.target.value)
                  setErrorCode(null)
                }}
              />
            </Field>
            <LoadingButton type="submit" className="w-full" disabled={otpCode.trim().length < 6} loading={submitting}>
              Verify & Create Account
            </LoadingButton>
          </form>

          <p className="text-sm text-muted-foreground">
            Didn’t get it?{' '}
            <button
              type="button"
              onClick={() => void handleSubmit(false)}
              disabled={submitting}
              className="font-medium text-primary hover:underline">
              Resend code
            </button>{' '}
            or{' '}
            <button
              type="button"
              onClick={() => {
                setOtpRequired(false)
                setOtpCode('')
                setErrorCode(null)
              }}
              className="font-medium text-primary hover:underline">
              use a different email
            </button>
            .
          </p>
        </Card>
      </PublicShell>
    )
  }

  return (
    <PublicShell center>
      <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground">
            Use the email address your invitation was sent to, and pick a password.
          </p>
        </header>

        {errorCode === 'exists' ? (
          <ExistingAccountAlert email={email} />
        ) : errorCode === 'not_invited' ? (
          <Alert variant="info" className="flex items-start gap-2">
            <Info className="mt-0.5 size-4 shrink-0" />
            <AlertDescription>
              {errorMessage ?? 'This email hasn’t been invited to Ordo yet.'}{' '}
              <a href="/get-started" className="font-medium underline">
                See if Ordo is at your school →
              </a>
            </AlertDescription>
          </Alert>
        ) : errorCode === 'unexpected' ? (
          <p className="text-sm text-destructive">{errorMessage ?? 'Unexpected error. Please try again.'}</p>
        ) : null}

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(false)
          }}>
          <Field>
            <FieldLabel htmlFor="signup-email">Email</FieldLabel>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                // Editing the address dismisses a stale exists/not-invited alert.
                setErrorCode(null)
              }}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="signup-first">First Name</FieldLabel>
              <Input
                id="signup-first"
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="signup-last">Last Name</FieldLabel>
              <Input
                id="signup-last"
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="signup-phone">Cell Phone Number</FieldLabel>
            <Input
              id="signup-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 555-5555"
              required
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="signup-password">Password</FieldLabel>
            <PasswordInput
              id="signup-password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">At least 8 characters.</p>
          </Field>
          <Field>
            <FieldLabel htmlFor="signup-confirm">Verify Password</FieldLabel>
            <PasswordInput
              id="signup-confirm"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm.length > 0 && !passwordsMatch ? (
              <p className="text-xs text-destructive">Passwords don’t match.</p>
            ) : null}
          </Field>
          <LoadingButton type="submit" className="w-full" disabled={!formValid} loading={submitting}>
            Continue
          </LoadingButton>
        </form>

        <p className="border-t border-border pt-4 text-sm text-muted-foreground">
          Already have an account?{' '}
          <a href={signinHrefForEmail(email)} className="font-medium text-primary hover:underline">
            Sign in here
          </a>
        </p>
      </Card>
    </PublicShell>
  )
}
